import React, { useEffect, useRef, useState } from "react";
import { Alert } from "@mui/material";
import { ShieldCheck } from "./icons";
import styles from "../publicvehicle.module.css";

const RESEND_SECONDS = 30;
const CODE_LENGTH = 6;

/*
 * Two-step phone verification card.
 * - askPhone: the visitor types the number the link was sent to (tenant link); otherwise the code goes to the
 *   number on file (guest link) and only phoneHint is shown.
 * - sendCode(phone) -> Promise<otpToken>, verifyCode(code, otpToken) -> Promise (reject with a message to show).
 */
const PhoneVerify = ({ askPhone, phoneHint, greeting, sendCode, verifyCode }) => {
  const [phone, setPhone] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const inputs = useRef([]);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const requestCode = async (e) => {
    if (e) e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const token = await sendCode(phone);
      setOtpToken(token);
      setDigits(Array(CODE_LENGTH).fill(""));
      setSecondsLeft(RESEND_SECONDS);
      setTimeout(() => inputs.current[0]?.focus(), 50);
    } catch (message) {
      setError(String(message));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    const code = digits.join("");
    if (code.length !== CODE_LENGTH) {
      setError("Please enter the 6-digit code.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await verifyCode(code, otpToken);
    } catch (message) {
      setError(String(message));
      setBusy(false);
    }
  };

  const setDigit = (index, value) => {
    const clean = value.replace(/\D/g, "");
    if (clean.length > 1) {
      // Pasted or autofilled code
      const next = clean.slice(0, CODE_LENGTH).split("");
      setDigits(Array(CODE_LENGTH).fill("").map((d, i) => next[i] || ""));
      inputs.current[Math.min(next.length, CODE_LENGTH) - 1]?.focus();
      return;
    }
    const next = digits.slice();
    next[index] = clean;
    setDigits(next);
    if (clean && index < CODE_LENGTH - 1) inputs.current[index + 1]?.focus();
  };

  const onKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) inputs.current[index - 1]?.focus();
  };

  if (!otpToken) {
    return (<form className={styles.card} onSubmit={requestCode} noValidate>
      <div className={styles.iconCircle}><ShieldCheck /></div>
      <h2 className={styles.cardTitle}>{greeting || "Verify your phone number"}</h2>
      <p className={styles.cardText}>
        {askPhone
          ? "For your security, enter the mobile number this link was sent to. We'll text you a 6-digit code."
          : `For your security, we'll text a 6-digit code to ${phoneHint || "the number you were invited on"}.`}
      </p>
      {askPhone && (<div className={styles.field}>
        <label htmlFor="verify-phone" className={styles.label}>Mobile number*</label>
        <div className={styles.phoneInput}>
          <span>+1</span>
          <input id="verify-phone" type="tel" inputMode="tel" autoComplete="tel-national" placeholder="(313) 555-0123" value={phone} disabled={busy} onChange={(e) => setPhone(e.target.value)} />
        </div>
      </div>)}
      {error !== "" && <Alert severity="error">{error}</Alert>}
      <button type="submit" className={styles.primaryButton} disabled={busy || (askPhone && phone.replace(/\D/g, "").length < 10)}>{busy ? "SENDING..." : "SEND CODE"}</button>
      <p className={styles.lockNote}>This link only works for the number it was sent to</p>
    </form>);
  }

  return (<form className={styles.card} onSubmit={submitCode} noValidate>
    <h2 className={styles.cardTitle}>Enter the 6-digit code</h2>
    <p className={styles.cardText}>We sent it to <strong>{phoneHint || "your phone"}</strong>.</p>
    <div role="group" aria-label="Verification code" className={styles.codeGrid}>
      {digits.map((digit, index) => (<input
        key={index}
        ref={(el) => { inputs.current[index] = el; }}
        aria-label={`Digit ${index + 1}`}
        inputMode="numeric"
        autoComplete={index === 0 ? "one-time-code" : "off"}
        maxLength={index === 0 ? CODE_LENGTH : 1}
        value={digit}
        disabled={busy}
        className={digit ? styles.codeFilled : ""}
        onChange={(e) => setDigit(index, e.target.value)}
        onKeyDown={(e) => onKeyDown(index, e)}
      />))}
    </div>
    <div className={styles.codeActions}>
      {secondsLeft > 0
        ? <span className={styles.muted}>Resend code in 0:{String(secondsLeft).padStart(2, "0")}</span>
        : <button type="button" className={styles.linkButton} onClick={() => requestCode()} disabled={busy}>Resend code</button>}
      {askPhone && <button type="button" className={styles.linkButton} onClick={() => { setOtpToken(""); setError(""); }} disabled={busy}>Change number</button>}
    </div>
    {error !== "" && <Alert severity="error">{error}</Alert>}
    <button type="submit" className={styles.primaryButton} disabled={busy || digits.join("").length !== CODE_LENGTH}>{busy ? "VERIFYING..." : "VERIFY & CONTINUE"}</button>
  </form>);
};

export default PhoneVerify;
